import { isApprover, isUploaderAllowed } from './AccessUtilities';
import { FRISCO_ROLE_RESTRICTED_APP_IDS, PROCESS_KEYS } from './Constants';
import appSettings from '../CorpReporting/app_settings';

/**
 * Encryption-enabled processes enforce strict uploader/approver-only access.
 * Portal admins, support admins, and corp admins do NOT override this policy.
 * This policy applies to any FRISCO with encryption_enabled === true,
 * regardless of app ID.
 */
const ENCRYPTION_PROCESS_POLICY = {
  id: 'encryption-process-roles-required',
  applies: ({ processData }) => {
    return processData?.[PROCESS_KEYS.encryption_enabled] === true
  },
  evaluate: async ({ processData, userId, currentStage, userLDAPS }) => {
    const [isUploader, isApproverUser] = await Promise.all([
      isUploaderAllowed(processData, userId, currentStage, userLDAPS),
      isApprover(processData, userId, currentStage, userLDAPS)
    ]);

    return {
      canUpload: isUploader,
      canViewHistory: isUploader || isApproverUser,
      canDownload: false, // Phase 1: download disabled for ALL users on encrypted processes
      hasUploaderAccess: isUploader,
      uploadDeniedReason: "Upload is restricted to configured uploaders for encryption-enabled processes.",
      historyDeniedReason: "History access for encryption-enabled processes is restricted to configured uploaders and approvers only. Admin roles do not override this restriction.",
      downloadDeniedReason: "File download is disabled for encryption-enabled processes in Phase 1."
    };
  }
};

const PROCESS_ROLE_POLICY = {
  id: 'process-roles-required',
  applies: ({ appId, currentStage }) =>
    FRISCO_ROLE_RESTRICTED_APP_IDS[currentStage]?.includes(String(appId))
    && (appSettings[currentStage]?.appId == null
      || String(appSettings[currentStage].appId) !== String(appId)),
  evaluate: async ({ processData, userId, currentStage, userLDAPS }) => {
    const [isUploader, isApproverUser] = await Promise.all([
      isUploaderAllowed(processData, userId, currentStage, userLDAPS),
      isApprover(processData, userId, currentStage, userLDAPS)
    ]);

    return {
      canUpload: isUploader,
      canViewHistory: isUploader || isApproverUser,
      hasUploaderAccess: isUploader,
      uploadDeniedReason: "You don't have permission to perform an upload action. You are not part of the Uploader group for this deliverable. If you believe you should have access, contact the process owner.",
      historyDeniedReason: "You don't have permission to view the history. You are not part of the Uploader/Approver group for this deliverable. If you believe you should have access, contact the process owner."
    };
  }
};

export const FRISCO_ACCESS_POLICIES = [ENCRYPTION_PROCESS_POLICY, PROCESS_ROLE_POLICY];

export const getApplicableFriscoAccessPolicies = (context) =>
  FRISCO_ACCESS_POLICIES.filter((policy) => policy.applies(context));

export const hasFriscoAccessRestrictions = (context) =>
  getApplicableFriscoAccessPolicies(context).length > 0;

export const resolveFriscoAccess = async (context) => {
  const policies = getApplicableFriscoAccessPolicies(context);
  if (policies.length === 0) {
    return { isRestricted: false, canUpload: true, canViewHistory: true, canDownload: true };
  }

  const results = await Promise.all(policies.map((policy) => policy.evaluate(context)));
  const deniedUpload = results.find((result) => result.canUpload === false);
  const deniedHistory = results.find((result) => result.canViewHistory === false);
  const deniedDownload = results.find((result) => result.canDownload === false);

  return {
    isRestricted: true,
    policyIds: policies.map((policy) => policy.id),
    canUpload: !deniedUpload,
    canViewHistory: !deniedHistory,
    canDownload: !deniedDownload,
    hasUploaderAccess: results.every((result) => result.hasUploaderAccess !== false),
    uploadDeniedReason: deniedUpload?.uploadDeniedReason,
    historyDeniedReason: deniedHistory?.historyDeniedReason,
    downloadDeniedReason: deniedDownload?.downloadDeniedReason
  };
};